// fallow-ignore-file code-duplication
import React, { useState, useMemo, useEffect, useRef } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import { ReportTableWrapper } from '@/components/reports/ReportTableWrapper';
import { useDivisionRebateSummary } from '@/api/rebates/useReports';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import type { ReportLineType, ReportTimeFilter } from '@erp/shared-types';
import {
  LINE_TYPE_LABELS,
  LINE_TYPE_DESCRIPTIONS,
} from '@/types/rebateAnalytics';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShareNetworkIcon, LinkIcon, XIcon } from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import { DownloadModal } from 'src/components/reports/DownloadModal';
import { downloadRebateMultiTableXlsx } from '@/components/rebates/reports/export/downloadRebateReportXlsx';
import { RebateReportDownloadMenu } from '@/components/rebates/reports/export/RebateReportDownloadMenu';
import {
  rebateMultiTableDownloadFilename,
  rebateMultiTableFinalFilename,
} from '@/components/rebates/reports/export/rebateMultiTableDownloadFilename';
import { downloadMultiTableCSV } from '@/utils/csvDownload';
import { getVisibleColumnIndices } from '@/utils/reportTableUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { T, Var, useGT, msg, useMessages } from 'gt-react';
import { HeaderPageTitle } from 'src/context/header-title.context';

const REPORT_ID = 'division-rebate-summary';

export interface DivisionRebateSummaryPageProps {
  lockedFilters?: {
    year: number;
    divisionIds?: string[];
    lineType?: ReportLineType;
    categoryFilters?: string[];
  };
}

const DivisionRebateSummaryPage: React.FC<DivisionRebateSummaryPageProps> = ({
  lockedFilters,
}) => {
  const gt = useGT();
  const m = useMessages();
  const currentYear = new Date().getFullYear();
  const isShared = !!lockedFilters;

  const reportTitle = gt('Division and Banner Rebate Summary', {
    context: 'report name, division and banner rebate summary',
  });

  const CATEGORY_OPTIONS: MultiSelectOption[] = useMemo(
    () => [
      {
        id: 'Redistribution',
        label: m(
          msg('Redistribution', {
            context: 'rebate report category filter option',
          }),
        ),
        value: 'Redistribution',
      },
      {
        id: 'Conditional',
        label: m(
          msg('Conditional', {
            context: 'rebate report category filter option',
          }),
        ),
        value: 'Conditional',
      },
      {
        id: 'Non-Conditional',
        label: m(
          msg('Non-Conditional', {
            context: 'rebate report category filter option',
          }),
        ),
        value: 'Non-Conditional',
      },
    ],
    [m],
  );
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    MultiSelectOption[]
  >([CATEGORY_OPTIONS[1]]); // Default to Conditional only
  const [lineType, setLineType] = useState<ReportLineType>('reported');
  const [ruleTagTimeFilter, setRuleTagTimeFilter] = useState<ReportTimeFilter>(
    currentYear === new Date().getFullYear() ? 'current' : 'all',
  );
  const [supplierTimeFilter, setSupplierTimeFilter] =
    useState<ReportTimeFilter>(
      currentYear === new Date().getFullYear() ? 'current' : 'all',
    );
  const hasSetDefaultDivision = useRef(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'csv' | 'xlsx'>('csv');

  const defaultFilename = useMemo(() => {
    const divisionPart =
      selectedDivisions.length > 0
        ? `-${selectedDivisions.map((d) => d.label.replace(/\s+/g, '-')).join('-')}`
        : '';
    return `division-rebate-summary-${selectedYear}${divisionPart}`;
  }, [selectedYear, selectedDivisions]);

  const handleDownloadClick = (format: 'csv' | 'xlsx') => {
    if (!report) return;
    setDownloadFormat(format);
    setDownloadModalOpen(true);
  };

  const handleDownload = (filename: string) => {
    if (!report) return;

    const ruleTagVisibleColumns = getVisibleColumnIndices(
      report.ruleTagTable,
      ruleTagTimeFilter,
      selectedYear,
    );
    const supplierVisibleColumns = getVisibleColumnIndices(
      report.supplierTable,
      supplierTimeFilter,
      selectedYear,
    );

    const options = {
      tables: [
        {
          title:
            report.ruleTagTable.title ||
            gt('Rebates by Rule Tag', {
              context: 'CSV export section title for rebates by rule tag table',
            }),
          table: report.ruleTagTable,
          rowLabelHeader: gt('Category', {
            context: 'CSV row label column header, rebate category',
          }),
          visibleColumnIndices: ruleTagVisibleColumns,
        },
        {
          title:
            report.supplierTable.title ||
            gt('Rebates by Supplier', {
              context: 'CSV export section title for rebates by supplier table',
            }),
          table: report.supplierTable,
          rowLabelHeader: gt('Supplier', {
            context: 'CSV row label column header, supplier name',
          }),
          visibleColumnIndices: supplierVisibleColumns,
        },
      ],
      filename: rebateMultiTableFinalFilename(filename, downloadFormat),
    };
    if (downloadFormat === 'xlsx') {
      void downloadRebateMultiTableXlsx(options);
      return;
    }
    downloadMultiTableCSV(options);
  };

  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  useEffect(() => {
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions(divisionOptions);
      hasSetDefaultDivision.current = true;
    }
  }, [divisionOptions, selectedDivisions.length, divisionsLoading]);

  useEffect(() => {
    const defaultTimeFilter: ReportTimeFilter =
      selectedYear < currentYear ? 'all' : 'current';
    setRuleTagTimeFilter(defaultTimeFilter);
    setSupplierTimeFilter(defaultTimeFilter);
  }, [selectedYear, currentYear]);

  const lineTypeOptions = useMemo(() => {
    return (['estimates', 'predictions', 'reported'] as const).map((type) => ({
      id: type,
      label: LINE_TYPE_LABELS[type] || type,
      value: type,
    }));
  }, []);

  const effectiveYear = lockedFilters?.year ?? selectedYear;
  const effectiveDivisionIds =
    lockedFilters?.divisionIds ??
    (selectedDivisions.length > 0
      ? selectedDivisions.map((d) => d.id)
      : undefined);
  const effectiveLineType = lockedFilters?.lineType ?? lineType;
  const effectiveCategoryFilters =
    lockedFilters?.categoryFilters ??
    (selectedCategories.length > 0 &&
    selectedCategories.length < CATEGORY_OPTIONS.length
      ? selectedCategories.map((c) => c.id)
      : undefined);

  const {
    data: report,
    isLoading,
    error,
  } = useDivisionRebateSummary({
    year: effectiveYear,
    divisionIds: effectiveDivisionIds,
    lineType: effectiveLineType,
    categoryFilters: effectiveCategoryFilters,
  });

  const reportContent = (
    <>
      {/* Loading State */}
      {isLoading && (
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>
                <T context="loading skeleton card title">Rebates by Rule Tag</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TableSkeleton />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                <T context="loading skeleton card title">Rebates by Supplier</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TableSkeleton />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">
              <T context="error message when rebate report fails to load">
                Error loading report data. Please try again.
              </T>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Report Tables */}
      {report && !isLoading && (
        <div className="space-y-3">
          <ReportTableWrapper
            table={report.ruleTagTable}
            timeFilter={ruleTagTimeFilter}
            onTimeFilterChange={setRuleTagTimeFilter}
            downloadFilename={`rebates-by-rule-tag-${effectiveYear}`}
            showTotals={true}
            year={effectiveYear}
            disableAllTimeFilter={effectiveLineType !== 'predictions'}
          />
          {!isShared && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                <T context="label before active category filter badges">
                  Filtering by:
                </T>
              </span>
              <div className="flex flex-wrap gap-1">
                {selectedCategories.length === CATEGORY_OPTIONS.length ? (
                  <Badge variant="secondary" className="text-xs">
                    <T context="badge when all rebate categories selected">
                      All Categories
                    </T>
                  </Badge>
                ) : selectedCategories.length === 0 ? (
                  <Badge variant="destructive" className="text-xs">
                    <T context="badge when no rebate categories selected">
                      No categories selected
                    </T>
                  </Badge>
                ) : (
                  selectedCategories.map((category) => (
                    <Badge
                      key={category.id}
                      variant="secondary"
                      className="text-xs"
                    >
                      {category.label}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          )}
          <ReportTableWrapper
            table={report.supplierTable}
            timeFilter={supplierTimeFilter}
            onTimeFilterChange={setSupplierTimeFilter}
            downloadFilename={`rebates-by-supplier-${effectiveYear}`}
            showTotals={true}
            year={effectiveYear}
            disableAllTimeFilter={effectiveLineType !== 'predictions'}
          />
        </div>
      )}
    </>
  );

  if (isShared) {
    return reportContent;
  }

  return (
    <DashboardLayout>
      <div className="p-1 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <HeaderPageTitle>
              <T context="report page title with year">
                Division and Banner Rebate Summary - <Var>{selectedYear}</Var>
              </T>
            </HeaderPageTitle>
            <p className="text-sm text-muted-foreground">
              <T context="report page subtitle">
                View rebate summaries aggregated by rule tags and suppliers
              </T>
            </p>
          </div>
          <div className="flex gap-2">
            <RebateReportDownloadMenu
              disabled={!report || isLoading}
              onDownload={handleDownloadClick}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="button to open share options for report">Share</T>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="menu item, create shareable report link">
                    Create share link
                  </T>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  <T context="menu item, manage existing shared links">
                    Manage shared links
                  </T>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters Card */}
        <Card className="mb-2">
          <CardHeader>
            <CardTitle>
              <T context="card title, report filter controls">Filters</T>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <SingleSelect
                label={gt('Year', {
                  context: 'filter label, calendar year',
                })}
                labelPosition="left"
                options={yearOptions}
                value={
                  yearOptions.find((opt) => opt.value === selectedYear) || null
                }
                onChange={(option) => {
                  if (option) {
                    setSelectedYear(option.value);
                  }
                }}
                placeholder={gt('Select year...', {
                  context: 'year select placeholder',
                })}
                searchPlaceholder={gt('Search years...', {
                  context: 'search years in select',
                })}
                shouldAutoSort={false}
              />
              <MultiSelect
                label={gt('Divisions', {
                  context: 'filter label, business divisions',
                })}
                labelPosition="left"
                options={divisionOptions}
                value={selectedDivisions}
                onChange={setSelectedDivisions}
                placeholder={gt('Select divisions...', {
                  context: 'multi-select placeholder',
                })}
                searchPlaceholder={gt('Search divisions...', {
                  context: 'search inside division multi-select',
                })}
                disabled={divisionsLoading}
              />
              <div>
                <SingleSelect
                  label={gt('Line Type', {
                    context: 'filter label, rebate line type',
                  })}
                  labelPosition="left"
                  options={lineTypeOptions}
                  value={
                    lineTypeOptions.find((opt) => opt.id === lineType) || null
                  }
                  onChange={(option) => {
                    if (option) {
                      setLineType(option.id as ReportLineType);
                    }
                  }}
                  placeholder={gt('Select line type...', {
                    context: 'line type select placeholder',
                  })}
                  searchPlaceholder={gt('Search line types...', {
                    context: 'search line types in select',
                  })}
                />
                {lineType && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {LINE_TYPE_DESCRIPTIONS[lineType]}
                  </p>
                )}
              </div>
              <MultiSelect
                label={gt('Categories', {
                  context: 'filter label, rebate categories',
                })}
                labelPosition="left"
                options={CATEGORY_OPTIONS}
                value={selectedCategories}
                onChange={setSelectedCategories}
                placeholder={gt('Select categories...', {
                  context: 'multi-select placeholder',
                })}
                searchPlaceholder={gt('Search categories...', {
                  context: 'search inside category multi-select',
                })}
              />
            </div>

            {/* Selected Divisions Pills */}
            {selectedDivisions.length > 0 && (
              <div className="pt-3 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  <T context="label showing count of selected divisions">
                    Selected Divisions (<Var>{selectedDivisions.length}</Var>)
                  </T>
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedDivisions.length === divisionOptions.length ? (
                    <Badge variant="secondary" className="gap-1 pl-3 pr-2 py-1">
                      <span>
                        <T context="badge when all divisions selected in filter">
                          All Divisions
                        </T>
                      </span>
                    </Badge>
                  ) : (
                    selectedDivisions.map((division) => (
                      <Badge
                        key={division.id}
                        variant="secondary"
                        className="gap-1 pl-3 pr-1 py-1"
                      >
                        <span>{division.label}</span>
                        <button
                          onClick={() =>
                            setSelectedDivisions(
                              selectedDivisions.filter(
                                (d) => d.id !== division.id,
                              ),
                            )
                          }
                          className="ml-1 hover:bg-muted rounded-full p-0.5"
                          title={`${gt('Remove', {
                            context:
                              'accessibility, remove selected division from filter',
                          })} ${division.label}`}
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {reportContent}
      </div>

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={reportTitle}
        initialYear={selectedYear}
        initialDivisions={selectedDivisions}
        initialLineType={lineType}
        customFilters={[
          {
            key: 'categoryFilters',
            label: gt('Categories', {
              context: 'share modal filter label, rebate categories',
            }),
            options: CATEGORY_OPTIONS.map((c) => ({
              id: c.id,
              label: c.label,
            })),
            initialValue: selectedCategories.map((c) => c.id),
            placeholder: gt('Select categories...', {
              context: 'share modal multi-select placeholder',
            }),
          },
        ]}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={reportTitle}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />
      <DownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownload}
        defaultFilename={rebateMultiTableDownloadFilename(
          defaultFilename,
          downloadFormat,
        )}
        title={
          downloadFormat === 'xlsx'
            ? gt('Download Division Rebate Summary (.xlsx)', {
                context:
                  'modal title for downloading division rebate summary as Excel',
              })
            : gt('Download Division Rebate Summary (.csv)', {
                context:
                  'modal title for downloading division rebate summary as CSV',
              })
        }
        confirmLabel={
          downloadFormat === 'xlsx'
            ? gt('Download Excel', {
                context: 'button to download full report as Excel',
              })
            : gt('Download CSV', {
                context: 'button to download full report as CSV',
              })
        }
      />
    </DashboardLayout>
  );
};

export default DivisionRebateSummaryPage;
